import React, { useState, useEffect } from "react";
import { X, Search } from "lucide-react";
import {
  useVendors,
  useProducts,
  useCreatePurchase,
  useUpdatePurchase,
} from "@/hooks/useInventory";
import { PurchaseFormData, VendorWithOutstanding, ProductWithStock, getStockStatusColor, InventoryPurchaseWithDetails } from "@/types/inventory";

type PurchaseFormModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
  purchaseId?: string;
  initialData?: InventoryPurchaseWithDetails;
};

export function PurchaseFormModal({
  isOpen,
  onClose,
  onSuccess,
  onError,
  purchaseId,
  initialData,
}: PurchaseFormModalProps) {
  const today = new Date().toISOString().split("T")[0];

  const [formData, setFormData] = useState<PurchaseFormData>({
    vendor_id: "",
    product_id: "",
    quantity: 1,
    cost_per_unit: 0,
    batch_number: "",
    expiry_date: "",
    purchase_date: today,
    invoice_number: "",
    notes: "",
    payment_status: "pending",
    amount_paid: undefined,
    payment_due_date: "",
  });

  // Load existing data when in edit mode
  useEffect(() => {
    if (initialData) {
      setFormData({
        vendor_id: initialData.vendor_id,
        product_id: initialData.product_id,
        quantity: initialData.quantity,
        cost_per_unit: initialData.cost_per_unit,
        batch_number: initialData.batch_number || "",
        expiry_date: initialData.expiry_date || "",
        purchase_date: initialData.purchase_date || today,
        invoice_number: initialData.invoice_number || "",
        notes: initialData.notes || "",
        payment_status: initialData.payment_status,
        amount_paid: initialData.amount_paid,
        payment_due_date: initialData.payment_due_date || "",
      });
    }
  }, [initialData, today]);

  const [vendorSearch, setVendorSearch] = useState("");
  const [productSearch, setProductSearch] = useState("");
  const [showVendorList, setShowVendorList] = useState(false);
  const [showProductList, setShowProductList] = useState(false);

  const { data: vendorsData } = useVendors({ is_active: true });
  const { data: productsData } = useProducts({ is_active: true });
  const createPurchase = useCreatePurchase();
  const updatePurchase = useUpdatePurchase(purchaseId || "");

  const mutation = purchaseId ? updatePurchase : createPurchase;

  const vendors = vendorsData?.vendors || [];
  const products = productsData?.products || [];

  const selectedVendor = vendors.find((v) => v.id === formData.vendor_id);
  const selectedProduct = products.find((p) => p.id === formData.product_id);

  // Calculate total cost
  const totalCost = formData.quantity * formData.cost_per_unit;

  // Filter vendors based on search
  const filteredVendors = vendors.filter((vendor) => {
    const searchLower = vendorSearch.toLowerCase();
    return (
      vendor.name.toLowerCase().includes(searchLower) ||
      vendor.company_name?.toLowerCase().includes(searchLower)
    );
  });

  // Filter products based on search
  const filteredProducts = products.filter((product) => {
    const searchLower = productSearch.toLowerCase();
    return (
      product.name.toLowerCase().includes(searchLower) ||
      product.brand?.toLowerCase().includes(searchLower) ||
      product.dosage?.toLowerCase().includes(searchLower)
    );
  });

  const handleSelectVendor = (vendor: VendorWithOutstanding) => {
    setFormData({ ...formData, vendor_id: vendor.id });
    setShowVendorList(false);
    setVendorSearch("");
  };

  const handleSelectProduct = (product: ProductWithStock) => {
    setFormData({ ...formData, product_id: product.id });
    setShowProductList(false);
    setProductSearch("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.vendor_id) {
      onError?.("Please select a vendor");
      return;
    }

    if (!formData.product_id) {
      onError?.("Please select a product");
      return;
    }

    if (formData.quantity <= 0) {
      onError?.("Quantity must be greater than 0");
      return;
    }

    if (formData.cost_per_unit < 0) {
      onError?.("Cost per unit cannot be negative");
      return;
    }

    if (formData.amount_paid !== undefined) {
      if (formData.amount_paid < 0) {
        onError?.("Amount paid cannot be negative");
        return;
      }
      if (formData.amount_paid > totalCost) {
        onError?.("Amount paid cannot exceed total cost");
        return;
      }
    }

    if (
      formData.payment_status === "paid" &&
      formData.amount_paid !== totalCost
    ) {
      onError?.("For paid status, amount paid must equal total cost");
      return;
    }

    try {
      // Clean up empty string dates - convert to undefined for API
      const cleanedData = {
        ...formData,
        expiry_date: formData.expiry_date || undefined,
        payment_due_date: formData.payment_due_date || undefined,
        batch_number: formData.batch_number || undefined,
        invoice_number: formData.invoice_number || undefined,
        notes: formData.notes || undefined,
      };

      await mutation.mutateAsync(cleanedData);
      onSuccess?.(
        purchaseId
          ? "Purchase updated successfully"
          : "Purchase recorded successfully"
      );
      onClose();
    } catch (error: any) {
      onError?.(
        error.message || `Failed to ${purchaseId ? "update" : "record"} purchase`
      );
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900">
            {purchaseId ? "Edit Purchase" : "Record Purchase"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Vendor Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Vendor *
            </label>
            {selectedVendor ? (
              <div className="border border-gray-300 rounded-lg p-3 flex justify-between items-center">
                <div>
                  <p className="font-medium text-gray-900">
                    {selectedVendor.name}
                  </p>
                  {selectedVendor.company_name && (
                    <p className="text-sm text-gray-500">
                      {selectedVendor.company_name}
                    </p>
                  )}
                  {selectedVendor.outstanding_amount > 0 && (
                    <p className="text-xs text-orange-600 mt-1">
                      Outstanding: ₹{selectedVendor.outstanding_amount.toFixed(0)}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setFormData({ ...formData, vendor_id: "" });
                  }}
                  className="text-red-600 hover:text-red-700 text-sm"
                >
                  Change
                </button>
              </div>
            ) : (
              <div className="relative">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={vendorSearch}
                    onChange={(e) => {
                      setVendorSearch(e.target.value);
                      setShowVendorList(true);
                    }}
                    onFocus={() => setShowVendorList(true)}
                    placeholder="Search vendors..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent"
                  />
                </div>

                {showVendorList && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {filteredVendors.length === 0 ? (
                      <div className="p-4 text-center text-gray-500">
                        No vendors found
                      </div>
                    ) : (
                      filteredVendors.map((vendor) => (
                        <button
                          key={vendor.id}
                          type="button"
                          onClick={() => handleSelectVendor(vendor)}
                          className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                        >
                          <p className="font-medium text-gray-900">
                            {vendor.name}
                          </p>
                          {vendor.company_name && (
                            <p className="text-sm text-gray-500">
                              {vendor.company_name}
                            </p>
                          )}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Product Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Product *
            </label>
            {selectedProduct ? (
              <div className="border border-gray-300 rounded-lg p-3 flex justify-between items-center">
                <div>
                  <p className="font-medium text-gray-900">
                    {selectedProduct.name}
                  </p>
                  <p className="text-sm text-gray-500">
                    {[
                      selectedProduct.brand,
                      selectedProduct.dosage,
                      selectedProduct.form,
                    ]
                      .filter(Boolean)
                      .join(" • ")}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStockStatusColor(
                        selectedProduct.stock_status
                      )}`}
                    >
                      Current Stock: {selectedProduct.current_stock}
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setFormData({ ...formData, product_id: "" });
                  }}
                  className="text-red-600 hover:text-red-700 text-sm"
                >
                  Change
                </button>
              </div>
            ) : (
              <div className="relative">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={productSearch}
                    onChange={(e) => {
                      setProductSearch(e.target.value);
                      setShowProductList(true);
                    }}
                    onFocus={() => setShowProductList(true)}
                    placeholder="Search products..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent"
                  />
                </div>

                {showProductList && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {filteredProducts.length === 0 ? (
                      <div className="p-4 text-center text-gray-500">
                        No products found
                      </div>
                    ) : (
                      filteredProducts.map((product) => (
                        <button
                          key={product.id}
                          type="button"
                          onClick={() => handleSelectProduct(product)}
                          className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                        >
                          <p className="font-medium text-gray-900">
                            {product.name}
                          </p>
                          <p className="text-sm text-gray-500">
                            {[product.brand, product.dosage, product.form]
                              .filter(Boolean)
                              .join(" • ")}
                          </p>
                          <span
                            className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${getStockStatusColor(
                              product.stock_status
                            )}`}
                          >
                            Stock: {product.current_stock}
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Quantity & Cost per Unit (side by side) */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Quantity *
              </label>
              <input
                type="number"
                min="1"
                value={formData.quantity}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    quantity: parseInt(e.target.value) || 1,
                  })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cost per Unit (₹) *
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={formData.cost_per_unit}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    cost_per_unit: parseFloat(e.target.value) || 0,
                  })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent"
                required
              />
            </div>
          </div>

          {/* Total Cost (Calculated) */}
          {formData.quantity > 0 && formData.cost_per_unit > 0 && (
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-sm text-gray-600">Total Cost</p>
              <p className="text-2xl font-bold text-gray-900">
                ₹{totalCost.toFixed(2)}
              </p>
            </div>
          )}

          {/* Batch Number & Expiry Date (side by side) */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Batch Number
              </label>
              <input
                type="text"
                value={formData.batch_number}
                onChange={(e) =>
                  setFormData({ ...formData, batch_number: e.target.value })
                }
                placeholder="e.g., BT2024-001"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Expiry Date
              </label>
              <input
                type="date"
                value={formData.expiry_date}
                onChange={(e) =>
                  setFormData({ ...formData, expiry_date: e.target.value })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent"
              />
            </div>
          </div>

          {/* Purchase Date & Invoice Number (side by side) */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Purchase Date
              </label>
              <input
                type="date"
                value={formData.purchase_date}
                onChange={(e) =>
                  setFormData({ ...formData, purchase_date: e.target.value })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Invoice Number
              </label>
              <input
                type="text"
                value={formData.invoice_number}
                onChange={(e) =>
                  setFormData({ ...formData, invoice_number: e.target.value })
                }
                placeholder="e.g., INV-2024-001"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent"
              />
            </div>
          </div>

          {/* Payment Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Payment Status
            </label>
            <select
              value={formData.payment_status}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  payment_status: e.target.value as
                    | "pending"
                    | "partial"
                    | "paid",
                })
              }
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent"
            >
              <option value="pending">Pending</option>
              <option value="partial">Partial</option>
              <option value="paid">Paid</option>
            </select>
          </div>

          {/* Amount Paid (conditional) */}
          {(formData.payment_status === "partial" ||
            formData.payment_status === "paid") && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Amount Paid (₹) *
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                max={totalCost}
                value={formData.amount_paid || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    amount_paid: e.target.value
                      ? parseFloat(e.target.value)
                      : undefined,
                  })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent"
                required
              />
              {formData.amount_paid !== undefined && formData.amount_paid < totalCost && (
                <p className="text-sm text-orange-600 mt-1">
                  Balance: ₹{(totalCost - formData.amount_paid).toFixed(2)}
                </p>
              )}
            </div>
          )}

          {/* Payment Due Date (conditional) */}
          {(formData.payment_status === "pending" ||
            formData.payment_status === "partial") && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Payment Due Date
              </label>
              <input
                type="date"
                value={formData.payment_due_date}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    payment_due_date: e.target.value,
                  })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent"
              />
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) =>
                setFormData({ ...formData, notes: e.target.value })
              }
              rows={2}
              placeholder="Purchase notes..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5B8E87] focus:border-transparent resize-none"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="flex-1 px-4 py-2 bg-[#5B8E87] text-white rounded-lg hover:bg-[#4A7069] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {mutation.isPending
                ? "Saving..."
                : purchaseId
                ? "Update Purchase"
                : "Record Purchase"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
